import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    signRequestId: String,
    locale: String,
    csrfToken: String,
    multiEnabled: Boolean,
    maxSize: Number,
    singleDocumentLimitMessage: String,
    invalidTypeMessage: String,
    tooLargeMessage: String,
    failedMessage: String
  }

  static targets = ["documentList", "fileInput", "uploadArea", "uploadProgress", "uploadError"]

  async uploadFile(event) {
    const files = event.target.files
    if (!files || files.length === 0) return

    this.clearError()

    // Check if single-doc plan and already has a document
    if (!this.multiEnabledValue) {
      const existing = this.documentListTarget.querySelectorAll("[data-document-id]")
      if (existing.length >= 1) {
        this.showError(this.singleDocumentLimitMessageValue)
        event.target.value = ""
        return
      }
    }

    this.showProgress()

    for (const file of files) {
      if (!file.name.toLowerCase().endsWith(".pdf")) {
        this.showError(this.invalidTypeMessageValue)
        continue
      }
      if (file.size > this.maxSizeValue * 1024 * 1024) {
        this.showError(this.tooLargeMessageValue)
        continue
      }

      await this.uploadSingleFile(file)
    }

    this.hideProgress()
    event.target.value = ""
  }

  async uploadSingleFile(file) {
    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/documents`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: formData
      })

      if (response.ok) {
        const doc = await response.json()
        this.addDocumentToList(doc)
      } else {
        this.showError(await this.errorMessageFrom(response))
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showError(this.failedMessageValue)
    }
  }

  // Extract a human-readable message from a failed upload response, falling
  // back to the generic failure message for non-JSON / unexpected bodies.
  async errorMessageFrom(response) {
    try {
      const payload = await response.json()
      if (Array.isArray(payload.errors) && payload.errors.length > 0) {
        return payload.errors.join(", ")
      }
      if (payload.error) return payload.error
    } catch (_) {
      // fall through to the generic message
    }
    return this.failedMessageValue
  }

  async removeDocument(event) {
    const docId = event.currentTarget.dataset.documentId
    if (!docId) return

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/documents/${docId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue
        }
      })

      if (response.ok) {
        const el = this.documentListTarget.querySelector(`[data-document-id="${docId}"]`)
        if (el) el.remove()
        this.hideSingleDocumentLimit()
      }
    } catch (error) {
      console.error("Remove error:", error)
    }
  }

  addDocumentToList(doc) {
    const el = document.createElement("div")
    el.className = "d-flex align-items-center flex-wrap gap-2 p-2 border rounded mb-1"
    el.dataset.documentId = doc.id
    el.innerHTML = `
      <i class="bi bi-file-earmark-pdf text-danger flex-shrink-0"></i>
      <span class="text-truncate">${this.escapeHtml(doc.filename)}</span>
      <span class="text-muted small text-nowrap">(${this.formatSize(doc.byte_size)})</span>
      ${doc.page_count ? `<span class="badge bg-secondary text-nowrap">${doc.page_count} pages</span>` : ""}
      <button type="button" class="btn btn-outline-danger btn-sm ms-auto flex-shrink-0" data-action="click->document-upload#removeDocument" data-document-id="${doc.id}">
        <i class="bi bi-x"></i>
      </button>
    `
    this.documentListTarget.appendChild(el)

    // Show single-document limit message if applicable
    if (!this.multiEnabledValue) {
      this.showSingleDocumentLimit()
    }
  }

  showProgress() {
    if (this.hasUploadProgressTarget) {
      this.uploadProgressTarget.classList.remove("d-none")
    }
  }

  hideProgress() {
    if (this.hasUploadProgressTarget) {
      this.uploadProgressTarget.classList.add("d-none")
    }
  }

  showError(message) {
    if (!this.hasUploadErrorTarget || !message) return
    this.uploadErrorTarget.textContent = message
    this.uploadErrorTarget.classList.remove("d-none")
  }

  clearError() {
    if (!this.hasUploadErrorTarget) return
    this.uploadErrorTarget.textContent = ""
    this.uploadErrorTarget.classList.add("d-none")
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  showSingleDocumentLimit() {
    if (!this.singleDocumentLimitMessageValue) return
    if (this.element.querySelector("[data-single-doc-limit]")) return

    const notice = document.createElement("span")
    notice.className = "text-warning"
    notice.dataset.singleDocLimit = true
    notice.textContent = this.singleDocumentLimitMessageValue

    const hint = this.element.querySelector(".form-text")
    if (hint) {
      hint.appendChild(document.createElement("br"))
      hint.appendChild(notice)
    }
  }

  hideSingleDocumentLimit() {
    const notice = this.element.querySelector("[data-single-doc-limit]")
    if (notice) {
      // Remove the preceding <br> if present
      if (notice.previousSibling && notice.previousSibling.nodeName === "BR") {
        notice.previousSibling.remove()
      }
      notice.remove()
    }
  }
}
